import { Sparkles, Link2, HeartHandshake, UsersRound, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { storyImage } from '../mock';
import { usePreviewValue } from '../contexts/CMSPreviewContext';
import { useI18n } from '../contexts/I18nContext';

const PILLARS = [
  { key: 'pillar1', icon: Link2, accent: '#E5614C' },
  { key: 'pillar2', icon: HeartHandshake, accent: '#FCD968' },
  { key: 'pillar3', icon: UsersRound, accent: '#5DCAB5' },
];

export default function StorySection() {
  const { t } = useI18n();
  const eyebrow = usePreviewValue('story', 'eyebrow', t('story.eyebrow'));
  const h1 = usePreviewValue('story', 'headline_1', t('story.h1'));
  const h2 = usePreviewValue('story', 'headline_2', t('story.h2'));
  const h3 = usePreviewValue('story', 'headline_3', t('story.h3'));
  const body1 = usePreviewValue('story', 'body_1', t('story.body1'));
  const body2 = usePreviewValue('story', 'body_2', t('story.body2'));
  const image = usePreviewValue('story', 'image', storyImage);
  const badgeLoc = usePreviewValue('story', 'badge_location', t('story.badgeLoc'));
  const badgeSub = usePreviewValue('story', 'badge_subtitle', t('story.badgeSub'));
  const ctaLabel = usePreviewValue('story', 'cta_label', t('story.cta'));

  return (
    <section id="about" className="py-20 lg:py-28 relative">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div className="relative">
          <div className="relative rounded-[28px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(42,27,26,0.35)]">
            <img src={image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${image}` : image} alt="GEMS @ The Arts in Framingham — a creative community space" className="w-full h-[560px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1B1A]/55 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
              <div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4" />
                  <span>{badgeLoc}</span>
                </div>
                <div className="font-serif-display text-2xl mt-1">{badgeSub}</div>
              </div>
              <div className="h-14 w-14 rounded-full bg-[#FCD968] flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-[#2A1B1A]" />
              </div>
            </div>
          </div>
          <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#5DCAB5]/30 blur-2xl" />
          <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-[#FCD968]/40 blur-2xl" />
        </div>

        <div>
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
            <span className="h-[2px] w-8 bg-[#E5614C]" /> {eyebrow}
          </div>
          <h2 className="font-serif-display text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] font-bold text-[#2A1B1A] mt-4">
            {h1} <span className="italic text-[#E5614C]">{h2}</span> {h3}
          </h2>
          <p className="mt-6 text-[#2A1B1A]/75 text-[16px] leading-relaxed">{body1}</p>
          <p className="mt-4 text-[#2A1B1A]/75 text-[16px] leading-relaxed">{body2}</p>

          <div className="grid gap-3 mt-8" data-testid="story-pillars">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.key}
                  className="group relative bg-white rounded-2xl p-4 border border-[#2A1B1A]/8 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(42,27,26,0.25)] transition-[transform,box-shadow] duration-300 flex items-center gap-4"
                >
                  <span
                    className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full"
                    style={{ backgroundColor: p.accent }}
                    aria-hidden="true"
                  />
                  <div
                    className="ml-2 h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${p.accent}1a`, color: p.accent }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: p.accent }}>
                      {t(`story.${p.key}.label`)}
                    </div>
                    <div className="font-serif-display text-[19px] sm:text-[20px] font-bold text-[#2A1B1A] leading-tight mt-0.5">
                      {t(`story.${p.key}.text`)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Button asChild className="mt-9 rounded-full bg-[#2A1B1A] hover:bg-[#3a2725] text-white font-semibold px-7 h-12 btn-press">
            <a href="#programs">{ctaLabel}</a>
          </Button>
        </div>
      </div>
    </section>
  );
}
